<template>
  <div>
    <b-card class="single-b-card" title="32-bit floating point number to Binary" align="left">
      <b-card-body>
        <table class="tutorial-table">
          <tr>
            <td class="input" colspan="3"><b-form-input v-model="floatValue" :state="floatingPointValid" placeholder="Enter a floating point value"/></td>
          </tr>
        </table>
        <table class="tutorial-table" :hidden="!floatingPointValid">
          <tr>
            <td class="ordered">&nbsp;</td>
            <td class="label">User Entry</td>
            <td class="numbers">{{ actualValue }}</td>
          </tr>
          <tr>
            <td class="ordered">1.</td>
            <td class="label">Convert decimal point to binary point</td>
            <td class="numbers">{{ binaryPoint }}</td>
          </tr>
          <tr>
            <td class="ordered">2.</td>
            <td class="label">Convert binary point to normalized scientific notation</td>
            <td class="numbers"><div v-html="normalizedScientificNotation"/></td>
          </tr>
          <tr>
            <td class="ordered">3.</td>
            <td class="label">Compute sign S: 0 = -ve, 1 = +ve</td>
            <td class="numbers">{{ signBinary }}</td>
          </tr>
          <tr>
            <td class="ordered">4.</td>
            <td class="label">Compute biased exponent E: E = n + 127</td>
            <td class="numbers">{{ biasedExponentBinary }}</td>
          </tr>
          <tr>
            <td class="ordered">5.</td>
            <td class="label">Compute mantissa M: everything after "1."</td>
            <td class="numbers">{{ mantissaBinary }}</td>
          </tr>
          <tr>
            <td class="ordered">6.</td>
            <td class="label">Put S, E, and M into 32-bit IEEE 754 format </td>
            <td class="numbers">{{ binaryValue }}</td>
          </tr>
          <tr>
            <td class="ordered">7.</td>
            <td class="label">Convert each 4-bit (nibble) to a hexadecimal digit</td>
            <td class="numbers">{{ hexValue }}</td>
          </tr>
        </table>
        <table class='ieee754' :hidden="!floatingPointValid">
          <tr>
            <td class="bits-label">S</td>
            <td class="bits-label">E</td>
            <td class="bits-label">M</td>
          </tr>
          <tr>
            <td class="bits">{{sign}}</td>
            <td class="bits">{{biasedExponentBinary}}</td>
            <td class="bits">{{mantissa}}</td>
          </tr>
        </table>
      </b-card-body>
    </b-card>
    <b-card class="single-b-card" title="Binary to 32-bit floating point number">
      <table class="tutorial-table">
        <tr>
          <td class="input" colspan="2"><b-form-input v-model="ieee754Value" :state="ieee754Valid" placeholder="Enter a binary or hexadecimal IEEE 754 encoded number"/></td>
        </tr>
        <tr>
          <td class="label"><span :hidden="!ieee754Valid">Floating Point Value</span></td>
          <td class="numbers"> <span :hidden="!ieee754Valid" class="single-result"><span @mouseover="hoverSign = true" @mouseleave="hoverSign = false">(-1)<sup>{{this.encodedS}}</sup></span> &times; 1.<span @mouseover="hoverMantissa = true" @mouseleave="hoverMantissa = false">{{this.encodedM}}</span> &times; 2<sup>(<span @mouseover="hoverExponent = true" @mouseleave="hoverExponent = false">{{this.encodedE}}</span> - 127)</sup> = {{ floatingPointValue }}</span></td>
        </tr>
                <tr>
          <td colspan="2">
            <table class='ieee754' :hidden="!ieee754Valid">
          <tr>
            <td class="bits-label">S</td>
            <td class="bits-label">E</td>
            <td class="bits-label">M</td>
          </tr>
          <tr>
            <td class="bits" :class="{ highlight_sign: hoverSign }">{{encoded[0]}}</td>
            <td class="bits" :class="{ highlight_exponent: hoverExponent }">{{encoded.substring(1, 9)}}</td>
            <td class="bits" :class="{ highlight_mantissa: hoverMantissa}">{{encoded.substring(9)}}</td>
          </tr>
        </table>
          </td>
        </tr>

      </table>
    </b-card>
</div>

  
</template>

<script>
import {NumberFormats} from '@/util/constants'

export default {
  name: 'ieee-754',
  data () {
    return {
      floatValue: '',
      ieee754Value: '',
      mantissa: '',
      biasedExponent: '',
      sign: 0,
      hoverSign: false,
      hoverExponent: false,
      hoverMantissa: false
    }
  },
  computed: {
    actualValue: function () {
      return this.floatingPointValid ? parseFloat(this.floatValue) : ''
    },
    binaryPoint: function () {
      if (!this.floatingPointValid) {
        return ''
      }

      var whole = parseFloat(this.floatValue)
      if (whole === 0.0) {
        this.biasedExponent = 0
        this.mantissa = '0'.repeat(23)
        return '0.0'
      }
      var bfraction = ''
      if (whole < 0) {
        this.sign = 1
        whole *= -1.0
      } else {
        this.sign = 0
      }

      var bwhole = (whole >>> 0).toString(2)
      var tooSmall = bwhole === '0'

      var fraction = whole - Math.floor(whole)
      for (var i = 0, f = 0.5; i < 23; i += tooSmall ? 0 : 1, f /= 2) {
        if (fraction - f < 0) {
          bfraction += '0'
        } else {
          bfraction += '1'
          if (tooSmall) {
            i--
          }
          tooSmall = false
          fraction -= f
        }
      }
      console.log('bits: ' + (10.1).toString(2))
      this.biasedExponent = bwhole.substring(1).length + 127
      this.mantissa = bwhole.substring(1) + bfraction.substring(0, bfraction.length - bwhole.substring(1).length)
      if (bwhole === '0') {
        var k
        for (k = -1; this.mantissa.substring(0, 1) === '0' && k > -23; k--) {
          this.mantissa = this.mantissa.substring(1) + '0'
        }
        this.biasedExponent += k
        this.mantissa = this.mantissa.substring(1) + '0'
      }
      this.mantissa = this.mantissa.substring(0, 23)
      if (fraction > (1.0 / Math.pow(2, bfraction.length + 1))) {
        this.mantissa = (parseInt(this.mantissa, 2) + 1).toString(2)
        this.mantissa = '0'.repeat(23 - this.mantissa.length) + this.mantissa
      }
      return (this.sign === 1 ? '-' : '') + bwhole + '.' + bfraction
    },
    biasedExponentBinary: function () {
      var bbiasedExponent = this.biasedExponent.toString(2)
      return this.floatingPointValid ? '0'.repeat(8 - bbiasedExponent.length) + bbiasedExponent : ''
    },
    mantissaBinary: function () {
      return this.floatingPointValid ? this.mantissa : ''
    },
    signBinary: function () {
      return this.floatingPointValid ? this.sign.toString() : ''
    },
    binaryValue: function () {
      var bvalue = this.ieee754.toString(2)
      return this.floatingPointValid ? 'b' + '0'.repeat(32 - bvalue.length) + bvalue : ''
    },
    hexValue: function () {
      var bvalue = this.ieee754.toString(16).toUpperCase()
      return this.floatingPointValid ? 'x' + ('0'.repeat(8 - bvalue.length)) + bvalue : ''
    },
    normalizedScientificNotation () {
      return this.floatingPointValid ? (this.sign === 1 ? '-' : '') + '1.' + this.mantissa.toString() + ' &times; 2<sup>' + (this.biasedExponent - 127).toString() + '</sup>' : ''
    },
    ieee754: function () {
      return this.floatingPointValid ? parseInt(this.sign.toString(2) + this.biasedExponentBinary + this.mantissa, 2) : ''
    },
    debug: function () {
      return this.biasedExponentBinary
    },
    floatingPointValid: function () {
      return this.floatValue.length < 1 ? null : NumberFormats.FLOATING_POINT_RE.test(this.floatValue)
    },
    ieee754Valid: function () {
      if (this.ieee754Value.length < 1) {
        return null
      } else if (NumberFormats.BINARY_RE.test(this.ieee754Value)) {
        return this.ieee754Value.length === 33
      } else if (NumberFormats.HEXADECIMAL_RE.test(this.ieee754Value)) {
        return this.ieee754Value.length === 9
      }
      return false
    },
    encoded: function () {
      var e = ''
      if (NumberFormats.HEXADECIMAL_RE.test(this.ieee754Value)) {
        e = parseInt(this.ieee754Value.substring(1), 16).toString(2)
      } else {
        e = this.ieee754Value.substring(1)
      }
      e = '0'.repeat(32 - e.length) + e
      return e
    },
    encodedS: function () {
      return parseInt(this.encoded[0], 2)
    },
    encodedE: function () {
      return parseInt(this.encoded.substring(1, 9), 2)
    },
    encodedM: function () {
      return this.encoded.substring(9)
    },
    floatingPointValue () {
      if (!this.ieee754Valid) {
        return ''
      }

      var encoded = ''
      if (NumberFormats.HEXADECIMAL_RE.test(this.ieee754Value)) {
        encoded = parseInt(this.ieee754Value.substring(1), 16).toString(2)
      } else {
        encoded = this.ieee754Value.substring(1)
      }
      encoded = '0'.repeat(32 - encoded.length) + encoded
      var s = parseInt(encoded[0], 2)
      var e = parseInt(encoded.substring(1, 9), 2)
      var m = 0x800000 + parseInt(encoded.substring(9), 2)
      return (Math.pow(-1, s) * (2 ** (e - 127)) * m / 0x800000).toString(10)
    }
  }
}
</script>

<style>
.numbers {
  text-align: left;
  vertical-align: top;
  color: black;
  font-family: math;
}

.ordered {
  vertical-align: top;
  width: 30px;
}

.card-title {
  text-align: left;
}

.input {
  padding-bottom: 1em;
}

.tutorial-table {
  text-align: left;
  min-width: 1085px;
  font-size: large;
}

.label {
  max-width: 230px;
  padding-top: 4px;
  vertical-align: top;
}

.ieee754 {
  margin-top: 2em;
  border: 1px dotted gray;
}

.bits {
  font-family: math;
  font-size: x-large;
  border: 1px dotted gray;
}

.bits-label {
  text-align: center;
  padding-left: 2px;
  border: 1px dotted gray;
  font-size: large;
}

.single-b-card {
  margin-bottom: 1em;;
}

.highlight_sign, .highlight_exponent, .highlight_mantissa {
  background: lightblue;
}
</style>



// WEBPACK FOOTER //
// src/components/single.vue